/**
 * C2 citation-revocation-chip state derivation.
 *
 * Pure function: (CitationView, now_ms) → ChipState.
 *
 * Decision tree (spec §4 + §5 + T-C2-3 placeholder header):
 *   artifact_status = 'published'                              → kind: none
 *   artifact_status = 'revoked', ack_status = 'pending'        → kind: revoked (action: acknowledge)
 *   artifact_status = 'revoked', ack_status = 'acknowledged'   → kind: tombstone_acknowledged
 *   artifact_status = 'revoked', ack_status = 'escalated'      → kind: escalated (action: escalate; owner contact)
 *   artifact_status = 'revoked', ack_status = 'pending',
 *     now > escalation_due_at                                  → kind: escalated (auto-promoted)
 */
import { CitationView, ChipState } from './types';

const ACK_ENDPOINT = '/api/workspace/artifact_citations/{citation_id}/acknowledge';
const ESCALATE_ENDPOINT = '/api/workspace/artifact_citations/{citation_id}/escalate';

export function deriveChipState(view: CitationView, now_ms: number): ChipState {
  if (view.artifact_status === 'published') {
    return {
      kind: 'none',
      label: '',
      aria_label: '',
      action: null,
    };
  }

  // artifact_status === 'revoked' beyond this point.
  if (view.ack_status === 'acknowledged') {
    return {
      kind: 'tombstone_acknowledged',
      label: 'Tombstone acknowledged',
      aria_label: 'Revoked artifact — tombstone acknowledged',
      reason: view.artifact_revocation_reason,
      action: null,
    };
  }

  const escalationDue = view.escalation_due_at;
  const inEscalation =
    view.ack_status === 'escalated' ||
    (view.ack_status === 'pending' && escalationDue !== undefined && now_ms > escalationDue);

  if (inEscalation) {
    return {
      kind: 'escalated',
      label: 'Revoked — escalation pending',
      aria_label: 'Revoked artifact — escalation pending; contact artifact owner',
      reason: view.artifact_revocation_reason,
      action: 'escalate',
      action_endpoint: endpointFor(ESCALATE_ENDPOINT, view.citation_id),
      action_label: 'Contact owner',
    };
  }

  // pending + within window
  return {
    kind: 'revoked',
    label: 'Revoked',
    aria_label: 'Revoked artifact — acknowledgement required',
    reason: view.artifact_revocation_reason,
    action: 'acknowledge',
    action_endpoint: endpointFor(ACK_ENDPOINT, view.citation_id),
    action_label: 'Acknowledge',
  };
}

function endpointFor(template: string, citation_id: string): string {
  return template.replace('{citation_id}', encodeURIComponent(citation_id));
}
